// @flow
import Debug from 'debug';
import type { Firestore } from '../../types/firebase';

const debug = Debug('amms:schema');
const rtDebug = Debug('rt:amms:schema');

type Statics = $ReadOnly<{
    [name: string]: () => any
}>;

export type Schema = $ReadOnly<{|
    collection: string,
    idKey?: string,
    statics?: Statics
|}>;

export type Config = $ReadOnly<{|
    statics?: Statics
|}>;

type Query = $ReadOnlyArray<$ReadOnlyArray<mixed>>

export type Model<Value> = $ReadOnly<{
    create: (data: Value) => Promise
}>;

type FindByIdAndUpdateOptions = $ReadOnly<{
    upsert?: boolean
}>;

type PagingOptions = $ReadOnly<{
    orderBy: string,
    itemsPerPage: number
}>;

type SubscribeProps = $ReadOnly<{
    query?: Query,
    paging?: PagingOptions
}>;

export default function createModel<Value>(_firestore?: Firestore, schema: Schema, config?: Config = {}): Model<Value> {
    if(!_firestore) {
        throw new Error('Firestore instance must be provided');
    }

    const firestore = _firestore;

    const {
        collection,
        idKey = 'id'
    } = schema;

    const getCollectionRef = (query) => {
        let ref = firestore.collection(collection);

        if(query) {
            for(let i = 0, length = query.length; i < length; i += 1) {
                ref = ref.where(...query[i]);
            }
        }

        return ref;
    };

    class Model {
        static create(data: Object, removeId?: boolean) {
            const id = data[idKey];
            const collectionRef = firestore.collection(collection);

            if(id) {
                let _data = data;
                if(removeId) {
                    _data = {
                        ...data
                    };
                    delete _data[idKey];
                }

                const docRef = collectionRef.doc(id);
                return new Promise((resolve, reject) => {
                    docRef.set(_data)
                        .then(() => resolve(docRef))
                        .catch(reject);
                });
            }
            else {
                return collectionRef.add(data);
            }
        }

        static find(query?: Query) {
            const ref = getCollectionRef(query);
            return ref.get();
        }

        static getRefById(id: string) {
            return firestore.collection(collection).doc(id);
        }

        static findById(id: string) {
            return Model.getRefById(id).get();
        }

        static getRefsByIds(ids: $ReadOnlyArray<string>) {
            return ids.map(id => firestore.collection(collection).doc(id));
        }

        static getAllByIds(ids: $ReadOnlyArray<string>) {
            const refs = Model.getRefsByIds(ids);

            return firestore.getAll(...refs);
        }

        static findByIdAndUpdate(id: string, data: Object, options?: FindByIdAndUpdateOptions) {
            debug(`findByIdAndUpdate ${collection}/${id}`, data);

            const opts = {
                upsert: false,
                ...options
            };

            if(opts.upsert) {
                return new Promise((resolve, reject) => {
                    firestore.collection(collection).doc(id).get()
                        .then(doc => {
                            if(doc.exists) {
                                firestore.collection(collection).doc(id).update(data)
                                    .then(resolve)
                                    .catch(reject);
                            }
                            else {
                                firestore.collection(collection).doc(id).set(data)
                                    .then(resolve)
                                    .catch(reject);
                            }
                        });
                });
            }
            else {
                return firestore.collection(collection).doc(id).update(data);
            }
        }

        static findByIdAndDelete(id: string) {
            return firestore.collection(collection).doc(id).delete();
        }

        static subscribe({ query, onLoad, onSubscribe, onAdd, onChange, onDelete, paging }: SubscribeProps) {
            debug(`Subscribing to collection ${collection}`);

            let ref = getCollectionRef(query);

            if(paging) {
                if(!paging.orderBy) {
                    throw new Error('You need to provide "orderBy" option for paging');
                }
                if(!paging.itemsPerPage) {
                    throw new Error('You need to provide "itemsPerPage" option for paging');
                }

                ref = ref
                    .orderBy(paging.orderBy)
                    .limit(paging.itemsPerPage);
            }

            const pagingContext = {
                page: 1,
                isLoading: false,
                queueCount: 0,
                last: false,
                lastDoc: null,
                subscribed: false,
                unsubscribes: []
            };



            const loadedIds = [];
            let initialized = false;
            const nextPage = () => {
                if(pagingContext.last) {
                    return;
                }

                if(pagingContext.isLoading) {
                    pagingContext.queueCount += 1;
                    return;
                }

                if(pagingContext.lastDoc) {
                    ref = ref.startAfter(pagingContext.lastDoc);
                }

                const subscribe = () => {
                    const unsubscribe = ref.onSnapshot(function(querySnapshot) {
                        debug(`Collection snapshot: ${collection}`);

                        if(onSubscribe && !pagingContext.subscribed) {
                            onSubscribe();
                        }

                        pagingContext.subscribed = true;

                        querySnapshot.docChanges().forEach((change) => {
                            const data = {
                                [idKey]: change.doc.id,
                                ...change.doc.data()
                            };

                            debug(`Document change: ${change.type} ${change.doc.id}`, data);

                            if(change.type === 'added' && onAdd) {
                                const loadedIndex = onLoad ? loadedIds.indexOf(change.doc.id) : -1;
                                if(loadedIndex !== -1) {
                                    debug(`Collection ${collection} ignoring first add event for loaded item ${change.doc.id}`);
                                    loadedIds.splice(loadedIndex, 1);
                                }
                                else {
                                    onAdd(data);
                                }
                            }
                            if(change.type === 'modified' && onChange) {
                                onChange(data);
                            }
                            if(change.type === 'removed'&& onDelete) {
                                onDelete(data);
                            }
                        });
                    });

                    pagingContext.unsubscribes.push(unsubscribe);
                };

                if(onLoad) {
                    pagingContext.isLoading = true;

                    if(pagingContext.queueCount > 0) {
                        pagingContext.queueCount -= 1;
                    }

                    if(!initialized) {
                        debug(`Loading first data: ${collection}`);
                    }
                    else {
                        debug(`Loading next page: ${collection}`);
                    }

                    debug(`Loading page ${pagingContext.page}`);

                    ref.get()
                        .then(querySnapshot => {
                            const data = [];
                            for(let i = 0, length = querySnapshot.docs.length; i < length; i += 1) {
                                const doc = querySnapshot.docs[i];
                                data.push({
                                    [idKey]: doc.id,
                                    ...doc.data()
                                });
                                loadedIds.push(doc.id);

                                if(i === length - 1) {
                                    pagingContext.lastDoc = doc;
                                }
                            }

                            if(paging && data.length < paging.itemsPerPage) {
                                pagingContext.last = true;
                            }

                            if(!initialized || data.length) {
                                if(!initialized) {
                                    debug(`First data loaded: ${collection}`);
                                    initialized = true;
                                }
                                else {
                                    debug(`Page ${pagingContext.page} loaded: ${collection}`);
                                }

                                pagingContext.page += 1;
                                onLoad(data, { last: pagingContext.last });
                            }



                            subscribe();

                            pagingContext.isLoading = false;

                            if(pagingContext.queueCount > 0) {
                                nextPage();
                            }
                        });
                }
                else {
                    initialized = true;
                    subscribe();
                }
            };


            const unsubscribe = () => {
                for(let i = 0, length = pagingContext.unsubscribes.length; i < length; i += 1) {
                    pagingContext.unsubscribes[i]();
                }
            };

            const next = () => {
                if(paging) {
                    nextPage();
                }
            };

            nextPage();

            return {
                unsubscribe,
                next
            };
        }

        static batchSetByIds(ids: $ReadOnlyArray<string>, data: Object, useSameData?: boolean) {
            const batch = firestore.batch();

            for(let i = 0, length = ids.length; i < length; i += 1) {
                const id = ids[i];

                const ref = Model.getRefById(id);
                batch.set(ref, useSameData ? data : data[id]);
            }

            return batch.commit();
        }

        static batchUpdateByIds(ids: $ReadOnlyArray<string>, data: Object, useSameData?: boolean) {
            const batch = firestore.batch();

            for(let i = 0, length = ids.length; i < length; i += 1) {
                const id = ids[i];

                const ref = Model.getRefById(id);
                batch.update(ref, useSameData ? data : data[id]);
            }

            return batch.commit();
        }

        static batchDeleteByIds(ids: $ReadOnlyArray<string>) {
            const batch = firestore.batch();

            for(let i = 0, length = ids.length; i < length; i += 1) {
                const id = ids[i];

                const ref = Model.getRefById(id);
                batch.delete(ref);
            }

            return batch.commit();
        }
    }

    const statics = {
        ...schema.statics,
        ...config.statics
    };
    if(statics) {
        const nativeStatics = Object.getOwnPropertyDescriptors(Model);

        for(const key in statics) {
            if(Object.prototype.hasOwnProperty.call(statics, key)) {
                if(nativeStatics[key]) {
                    throw new Error(`"${key}" is reserved word and cannot be used as a static property name`);
                }

                if(typeof statics[key] === 'function') {
                    Model[key] = (...args) => statics[key](Model, ...args);
                }
                else {
                    Model[key] = statics[key];
                }
            }
        }
    }

    return Model;
}